/**
 * Pure readiness helpers — no react-admin or MUI imports.
 * Safe to import in tests without mocking.
 */

import type { Event, ScoreTable } from '../types';
import { hasDemographicScoreFields } from './scoreTableHelpers';

export type EventReadiness = 'complete' | 'needs_table' | 'no_table_needed';
export type AssessmentReadiness = 'draft' | 'needs_scoring' | 'ready';

export interface ReadinessCoverage {
  covered: number;
  total: number;
}

/**
 * Returns the scoring readiness of an event given the available score tables.
 */
export function eventScoringReadiness(
  event: Event,
  scoreTables: ScoreTable[],
): EventReadiness {
  // Explicit pass_fail mode
  if (event.scoring_mode === 'pass_fail') return 'no_table_needed';

  // Legacy: no scoring_mode, no measurements, scoring_type is pass_fail
  if (
    !event.scoring_mode &&
    (!event.measurements || event.measurements.length === 0) &&
    event.scoring_type === 'pass_fail'
  ) {
    return 'no_table_needed';
  }

  const measurements = event.measurements ?? [];

  // No measurements and not explicitly pass_fail → needs a table
  if (measurements.length === 0) return 'needs_table';

  // pass_fail-KIND measurements can never have a scoring table, so they must
  // not count against coverage — otherwise a mixed event (e.g. a pass/fail
  // "Completed" check alongside a timed measurement) can never reach
  // 'complete' and shows "Needs scoring table" forever.
  const tableMeasurements = measurements.filter((m) => m.kind !== 'pass_fail');

  // Every measurement is pass_fail → nothing to cover
  if (tableMeasurements.length === 0) return 'no_table_needed';

  // For each table-requiring measurement, check for an active ScoreTable
  const allCovered = tableMeasurements.every((m) =>
    scoreTables.some(
      (st) =>
        st.event_id === event.id &&
        st.measurement_id === m.measurement_id &&
        st.status === 'active' &&
        !hasDemographicScoreFields(st.rows),
    ),
  );

  return allCovered ? 'complete' : 'needs_table';
}

/**
 * Returns the overall readiness of an assessment given its events and score tables.
 */
export function assessmentReadiness(
  assessment: { events?: Array<{ event_id: string }> } | null | undefined,
  events: Event[],
  scoreTables: ScoreTable[],
): { readiness: AssessmentReadiness; coverage: ReadinessCoverage } {
  if (!assessment?.events?.length) {
    return { readiness: 'draft', coverage: { covered: 0, total: 0 } };
  }

  const assessmentEventIds = assessment.events.map((e) => e.event_id);

  let total = 0;
  let covered = 0;

  for (const eventId of assessmentEventIds) {
    const event = events.find((e) => e.id === eventId);
    if (!event) continue;

    const eventTables = scoreTables.filter((st) => st.event_id === eventId);
    const readiness = eventScoringReadiness(event, eventTables);

    if (readiness === 'no_table_needed') {
      // Does not count toward total
      continue;
    }

    total++;
    if (readiness === 'complete') {
      covered++;
    }
  }

  // All events are pass/fail or legacy non-measured
  if (total === 0) {
    return { readiness: 'ready', coverage: { covered: 0, total: 0 } };
  }

  if (covered < total) {
    return { readiness: 'needs_scoring', coverage: { covered, total } };
  }

  return { readiness: 'ready', coverage: { covered, total } };
}
